<template>
  <div class="fm-page-shell">
    <!-- ===== MAIN ===== -->
    <main class="fm-main">
      <b-overlay :show="loading" spinner small>
        <!-- Section tabs: Your Services / Access Requests / Manage Access -->
        <div class="fm-tabs">
          <button type="button" class="fm-tab" :class="{ 'fm-tab--active': activeView === 'services' }"
                  @click="activeView = 'services'">
            <i class="fa-solid fa-shield-halved"></i>Your Services
            <span class="fm-tab-count">{{ serviceAccessList.length }}</span>
          </button>
          <button v-if="isAdmin" type="button" class="fm-tab" :class="{ 'fm-tab--active': activeView === 'requests' }"
                  @click="selectRequestsView">
            <i class="fa-solid fa-inbox"></i>Access Requests
            <span class="fm-tab-count">{{ pendingRequests.length }}</span>
          </button>
          <button v-if="isAdmin" type="button" class="fm-tab" :class="{ 'fm-tab--active': activeView === 'manage' }"
                  @click="activeView = 'manage'">
            <i class="fa-solid fa-user-gear"></i>Manage Access
            <span class="fm-tab-count">{{ accessRecords.length }}</span>
          </button>
        </div>
        <div class="fm-page">
          <div class="fm-page-head">
            <div class="fm-page-title">Service Access</div>
            <button class="fm-btn fm-btn--primary" @click="openGrantModal">
              <i class="fa-solid fa-plus"></i>{{ isAdmin ? 'Grant Access' : 'Request Access' }}
            </button>
          </div>
          <div class="fm-page-desc">{{ viewDescription }}</div>

          <!-- ===== YOUR SERVICES ===== -->
          <div v-if="activeView === 'services'">
            <div v-if="serviceAccessList.length > 0" class="fm-toolbar">
              <div class="fm-search">
                <i class="fa-solid fa-magnifying-glass"></i>
                <b-form-input type="search" v-model="servicesSearch" placeholder="Search services..."></b-form-input>
              </div>
              <div v-if="filteredServiceAccessList.length > servicesPerPage" class="fm-toolbar-pager">
                <b-pagination v-model="servicesPage" :total-rows="filteredServiceAccessList.length"
                              :per-page="servicesPerPage" size="sm" />
              </div>
            </div>
            <div v-if="pagedServiceAccessList.length > 0" class="fm-list">
              <div v-for="row in pagedServiceAccessList" :key="row.Sid || row.ServiceName" class="fm-row" @click="openRow(row)">
                <div class="fm-row-main">
                  <div class="fm-row-top">
                    <span class="fm-row-name">{{ row.ServiceName }}</span>
                    <i class="fa-regular fa-copy fm-row-copy"></i>
                    <span v-if="row.ServiceType" class="fm-tag">{{ row.ServiceType }}</span>
                    <span v-if="row.ServiceSubType" class="fm-tag">{{ row.ServiceSubType }}</span>
                  </div>
                  <div class="fm-row-desc">Expires · {{ row.IsTemporary ? formatDateTime(row.ExpiresAt, row.ExpiresTime) : 'Never' }}</div>
                </div>
                <span v-if="row.Role" class="fm-value-pill">{{ row.Role }}</span>
                <div class="fm-status-cell">
                  <span class="fm-status-pill" :class="'fm-status-pill--' + accessTone(row)">
                    <span v-if="accessTone(row) !== 'neu'" class="fm-dot"></span>{{ row.IsTemporary ? 'Temporary' : 'Permanent' }}
                  </span>
                </div>
                <i class="fa-solid fa-angle-right fm-chev"></i>
              </div>
            </div>
            <div v-else-if="serviceAccessList.length > 0" class="fm-empty">No services match your search.</div>
            <div v-else class="access-empty">
              <i class="fa-solid fa-shield-halved"></i>
              <div class="access-empty__title">No services yet</div>
              <div class="access-empty__sub">{{ isAdmin ? 'Services granted to you will appear here.' : "You don't have access to any services yet." }}</div>
              <button v-if="!isAdmin" class="fm-btn fm-btn--primary mt-3" @click="openGrantModal">
                <i class="fa-solid fa-plus"></i>Request access
              </button>
            </div>
          </div>

          <!-- ===== ACCESS REQUESTS (approval inbox) ===== -->
          <div v-else-if="activeView === 'requests'">
            <div v-if="sortedRequests.length > 0" class="fm-list">
              <div v-for="row in sortedRequests" :key="row.RequestId"
                   class="fm-row" :class="{ 'req-row--pending': row.Status === 'pending' }"
                   @click="openRequest(row)">
                <div class="fm-row-main">
                  <div class="fm-row-top">
                    <span class="fm-row-name">{{ row.Service.Name }}</span>
                    <span class="fm-tag">{{ row.IsTemporary ? 'Temporary' : 'Permanent' }}</span>
                  </div>
                  <div class="fm-row-desc">{{ row.Reason ? row.Reason : 'No reason provided' }}</div>
                </div>
                <span v-if="row.Status !== 'pending' && row.ApprovalBy" class="fm-value-pill">{{ row.ApprovalBy }}</span>
                <div class="fm-status-cell">
                  <span class="fm-status-pill" :class="'fm-status-pill--' + requestTone(row)">
                    <span v-if="requestTone(row) !== 'neu'" class="fm-dot"></span>{{ row.Status }}
                  </span>
                </div>
                <div v-if="row.Status === 'pending'" class="req-actions" @click.stop>
                  <button class="fm-btn fm-btn--primary" @click="approveRequest(row)"><i class="fa-solid fa-check"></i>Approve</button>
                  <button class="fm-btn fm-btn--danger" @click="rejectRequest(row.RequestId)"><i class="fa-solid fa-xmark"></i>Reject</button>
                </div>
                <i v-else class="fa-solid fa-angle-right fm-chev"></i>
              </div>
            </div>
            <div v-else class="access-empty">
              <i class="fa-regular fa-circle-check"></i>
              <div class="access-empty__title">You're all caught up</div>
              <div class="access-empty__sub">New access requests will show up here for you to approve.</div>
            </div>
          </div>

          <!-- ===== MANAGE ACCESS ===== -->
          <div v-else-if="activeView === 'manage'">
            <div class="manage-picker">
              <label class="manage-picker__label" for="user-select">User</label>
              <v-select
                id="user-select"
                class="manage-picker__select"
                v-model="manageAccessUser"
                :options="eligibleUsers"
                placeholder="Select a user to manage"
                @input="loadAccessRecords"
              >
                <template #first>
                  <option value="">-- Select a user --</option>
                </template>
              </v-select>
            </div>

            <template v-if="manageAccessUser">
              <div v-if="accessRecords.length > 0">
                <div class="fm-toolbar">
                  <div class="fm-search">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    <b-form-input type="search" v-model="manageSearch" placeholder="Search services..."></b-form-input>
                  </div>
                  <div v-if="filteredAccessRecords.length > managePerPage" class="fm-toolbar-pager">
                    <b-pagination v-model="managePage" :total-rows="filteredAccessRecords.length"
                                  :per-page="managePerPage" size="sm" />
                  </div>
                </div>
                <!-- Selection bar -->
                <div class="manage-bar">
                  <b-form-checkbox
                    v-model="selectAll"
                    :indeterminate="indeterminate"
                    @change="selectAllRows"
                  >{{ selectedAccessIds.length > 0 ? selectedAccessIds.length + ' selected' : 'Select all' }}</b-form-checkbox>
                  <div v-if="selectedAccessIds.length > 0" class="manage-bar__actions">
                    <button class="fm-btn fm-btn--ghost" @click="showBulkExtendModal"><i class="fa-solid fa-clock"></i>Extend</button>
                    <button class="fm-btn fm-btn--danger" @click="showBulkRevokeModal"><i class="fa-solid fa-xmark"></i>Revoke</button>
                  </div>
                </div>

                <div v-if="pagedAccessRecords.length > 0" class="fm-list">
                  <div v-for="row in pagedAccessRecords" :key="row.Sid" class="fm-row" @click="openManage(row)">
                    <div class="fm-row-check" @click.stop>
                      <b-form-checkbox
                        :checked="selectedAccessIds.includes(row.Sid)"
                        @change="toggleRowSelection(row.Sid)"
                      ></b-form-checkbox>
                    </div>
                    <div class="fm-row-main">
                      <div class="fm-row-top">
                        <span class="fm-row-name">{{ row.ServiceName }}</span>
                        <span v-if="row.ServiceType" class="fm-tag">{{ row.ServiceType }}</span>
                        <span v-if="row.ServiceSubType" class="fm-tag">{{ row.ServiceSubType }}</span>
                      </div>
                      <div class="fm-row-desc">
                        <span v-if="row.IsTemporary">Expires · {{ formatDateTime(row.ExpiresAt, row.ExpiresTime) }} · {{ getTimeRemaining(row) }}</span>
                        <span v-else>Expires · Never</span>
                      </div>
                    </div>
                    <span v-if="row.Role" class="fm-value-pill">{{ row.Role }}</span>
                    <div class="fm-status-cell">
                      <span class="fm-status-pill" :class="'fm-status-pill--' + accessTone(row)">
                        <span v-if="accessTone(row) !== 'neu'" class="fm-dot"></span>{{ row.IsTemporary ? 'Temporary' : 'Permanent' }}
                      </span>
                    </div>
                    <i class="fa-solid fa-angle-right fm-chev"></i>
                  </div>
                </div>
                <div v-else class="fm-empty">No services match your search.</div>
              </div>
              <div v-else class="access-empty">
                <i class="fa-solid fa-user-lock"></i>
                <div class="access-empty__title">No access grants</div>
                <div class="access-empty__sub">This user doesn't have access to any services yet.</div>
              </div>
            </template>
            <div v-else class="access-empty">
              <i class="fa-solid fa-user-gear"></i>
              <div class="access-empty__title">Pick a user</div>
              <div class="access-empty__sub">Choose a user to review and manage the services they can reach.</div>
            </div>
          </div>
        </div>

        <!-- Approval Modal -->
        <b-modal id="assign-role-modal" ref="assignRoleModal" title="Assign Role" @ok="confirmApproval"
                 ok-variant="primary">
          <b-form-group label="Service" label-for="service-select">
            <v-select id="service-select" v-model="selectedServiceId" :options="services" disabled>
              <template v-slot:first>
                <option :value="null" disabled>Select a service</option>
              </template>
            </v-select>
          </b-form-group>

          <!-- Conditionally display roles dropdown if roles are available -->
          <div v-if="roles.length > 0">
            <b-form-group label="Assign Role" label-for="role-select">
              <v-select id="role-select" v-model="selectedRoleId" :options="roles" required>
                <template v-slot:first>
                  <option :value="null" disabled>Select a role</option>
                </template>
              </v-select>
            </b-form-group>
          </div>

          <p v-else>No roles available for this service.</p>
        </b-modal>

        <!-- Revoke Confirmation Modal -->
        <b-modal id="confirm-revoke-modal" ref="confirmRevokeModal" title="Confirm Revoke Access" @ok="revokeAccess"
                 ok-variant="danger btn  btn-fill" cancel-variant="btn btn-fill">
          <p class="my-2">Are you sure you want to revoke access?</p>
        </b-modal>

        <!-- Extend Access Modal -->
        <b-modal id="extend-access-modal" ref="extendAccessModal" title="Extend Access" @ok="extendAccess"
                 ok-variant="primary btn btn-fill" cancel-variant="btn btn-fill">
          <div v-if="accessToExtend">
            <p class="mb-3">Extending access for <strong>{{ accessToExtend.ServiceName }}</strong></p>
            
            <div class="form-group">
              <label>Quick Duration Options</label>
              <div class="quick-duration-buttons mb-3">
                <b-button-group size="sm">
                  <b-button 
                    v-for="duration in quickDurations" 
                    :key="duration.value"
                    variant="outline-primary"
                    @click="setExtensionQuickDuration(duration)"
                    class="quick-duration-btn"
                  >
                    {{ duration.label }}
                  </b-button>
                </b-button-group>
              </div>
              
              <label>Or specify new expiration</label>
              <div class="form-group">
                <b-form-input
                  v-model="extensionData.expirationDateTime"
                  type="datetime-local"
                  class="filter-input"
                  :min="minDateTime"
                  required
                />
              </div>
            </div>
            
            <div class="form-group">
              <label>Reason for extension</label>
              <b-form-textarea
                v-model="extensionData.reason"
                placeholder="Enter reason for extending access..."
                rows="3"
              ></b-form-textarea>
            </div>
          </div>
        </b-modal>

        <!-- Bulk Extend Modal -->
        <b-modal id="bulk-extend-modal" ref="bulkExtendModal" title="Bulk Extend Access" @ok="bulkExtendAccess"
                 ok-variant="warning btn btn-fill" cancel-variant="btn btn-fill">
          <p class="mb-3">Extending access for <strong>{{ (selectedAccessIds || []).length }}</strong> selected items</p>
          
          <div class="form-group">
            <label>Quick Duration Options</label>
            <div class="quick-duration-buttons mb-3">
              <b-button-group size="sm">
                <b-button 
                  v-for="duration in quickDurations" 
                  :key="duration.value"
                  variant="outline-primary"
                  @click="setBulkExtensionQuickDuration(duration)"
                  class="quick-duration-btn"
                >
                  {{ duration.label }}
                </b-button>
              </b-button-group>
            </div>
            
            <label>Or specify new expiration</label>
            <div class="form-group">
              <b-form-input
                v-model="bulkExtensionData.expirationDateTime"
                type="datetime-local"
                class="filter-input"
                :min="minDateTime"
                required
              />
            </div>
          </div>
          
          <div class="form-group">
            <label>Reason for bulk extension</label>
            <b-form-textarea
              v-model="bulkExtensionData.reason"
              placeholder="Enter reason for bulk extension..."
              rows="3"
            ></b-form-textarea>
          </div>
        </b-modal>

        <!-- Bulk Revoke Modal -->
        <b-modal id="bulk-revoke-modal" ref="bulkRevokeModal" title="Bulk Revoke Access" @ok="bulkRevokeAccess"
                 ok-variant="danger btn btn-fill" cancel-variant="btn btn-fill">
          <p class="mb-3">Are you sure you want to revoke access for <strong>{{ (selectedAccessIds || []).length }}</strong> selected items?</p>
          
          <div class="form-group">
            <label>Reason for bulk revocation</label>
            <b-form-textarea
              v-model="bulkRevokeData.reason"
              placeholder="Enter reason for bulk revocation..."
              rows="3"
            ></b-form-textarea>
          </div>
        </b-modal>


        <b-modal v-model="showGrantModal" :title="isAdmin ? 'Grant Access' : 'Request Access'" hide-footer>
          <div class="access-form">
            <!-- Admin grants access to a user; members request it for themselves -->
            <b-form v-if="isAdmin" @submit.stop.prevent="handleGrantAccess">
              <p class="access-form__intro">Give a user access to a service. Temporary grants expire on their own.</p>

              <b-form-group label="Service" label-for="grant-service">
                <v-select
                  id="grant-service"
                  v-model="newGrantService"
                  :options="serviceSearchResults"
                  :loading="serviceSearchLoading"
                  @input="fetchRolesForService"
                  @search="searchServices"
                  placeholder="Type to search for services..."
                  :filterable="false"
                  :clearable="true"
                >
                  <template #no-options="{ search }">
                    <div class="text-center py-2">
                      <span v-if="serviceSearchLoading">Searching...</span>
                      <span v-else-if="!search || search.length === 0">Type to search for services</span>
                      <span v-else>No services found for "{{ search }}"</span>
                    </div>
                  </template>
                  <template #spinner="{ loading }">
                    <div v-if="loading" class="vs__spinner" style="border-left-color: #2563EB;">
                      <div class="vs__spinner-circle"></div>
                    </div>
                  </template>
                </v-select>
              </b-form-group>

              <b-form-group v-if="roles.length > 0" label="Role" label-for="grant-role">
                <v-select id="grant-role" v-model="newGrantRole" :options="roles" placeholder="Select a role"></v-select>
              </b-form-group>

              <b-form-group label="User" label-for="grant-user">
                <v-select id="grant-user" v-model="newGrantUser" :options="eligibleUsers" placeholder="Select a user"></v-select>
              </b-form-group>

              <b-form-group label="Access duration">
                <b-form-checkbox v-model="newGrant.isTemporary" switch class="access-form__switch">
                  Temporary access
                </b-form-checkbox>
                <div v-if="newGrant.isTemporary" class="access-form__duration">
                  <div class="quick-duration-buttons">
                    <b-button-group size="sm">
                      <b-button
                        v-for="duration in quickDurations"
                        :key="duration.value + duration.unit"
                        variant="outline-primary"
                        class="quick-duration-btn"
                        @click="setQuickDuration(duration, 'grant')"
                      >{{ duration.label }}</b-button>
                    </b-button-group>
                  </div>
                  <b-form-input
                    v-model="newGrant.expirationDateTime"
                    type="datetime-local"
                    class="filter-input"
                    :min="minDateTime"
                  />
                  <small class="form-text text-muted">Pick a preset or set a custom expiry.</small>
                </div>
              </b-form-group>

              <div class="fm-modal-foot">
                <button type="button" class="fm-btn fm-btn--ghost" @click="showGrantModal = false">Cancel</button>
                <button type="submit" class="fm-btn fm-btn--primary" :disabled="!canGrant">Grant Access</button>
              </div>
            </b-form>

            <!-- Member: request access -->
            <b-form v-else @submit.stop.prevent="handleRequestAccess">
              <p class="access-form__intro">Ask an admin for access to a service. They'll review your request.</p>

              <b-form-group label="Service" label-for="req-service">
                <v-select
                  id="req-service"
                  v-model="newReqServiceId"
                  :options="serviceSearchResults"
                  :loading="serviceSearchLoading"
                  @search="searchServices"
                  placeholder="Type to search for services..."
                  :filterable="false"
                  :clearable="true"
                >
                  <template #no-options="{ search }">
                    <div class="text-center py-2">
                      <span v-if="serviceSearchLoading">Searching...</span>
                      <span v-else-if="!search || search.length === 0">Type to search for services</span>
                      <span v-else>No services found for "{{ search }}"</span>
                    </div>
                  </template>
                  <template #spinner="{ loading }">
                    <div v-if="loading" class="vs__spinner" style="border-left-color: #2563EB;">
                      <div class="vs__spinner-circle"></div>
                    </div>
                  </template>
                </v-select>
              </b-form-group>

              <b-form-group label="Reason for access" label-for="req-reason">
                <b-form-textarea
                  id="req-reason"
                  v-model="newRequest.reason"
                  placeholder="Explain why you need access..."
                  rows="4"
                ></b-form-textarea>
                <small class="form-text text-muted">Context helps admins approve faster.</small>
              </b-form-group>

              <b-form-group label="Access duration">
                <b-form-checkbox v-model="newRequest.isTemporary" switch class="access-form__switch">
                  Temporary access
                </b-form-checkbox>
                <div v-if="newRequest.isTemporary" class="access-form__duration">
                  <div class="quick-duration-buttons">
                    <b-button-group size="sm">
                      <b-button
                        v-for="duration in quickDurations"
                        :key="duration.value + duration.unit"
                        variant="outline-primary"
                        class="quick-duration-btn"
                        @click="setQuickDuration(duration, 'request')"
                      >{{ duration.label }}</b-button>
                    </b-button-group>
                  </div>
                  <b-form-input
                    v-model="newRequest.expirationDateTime"
                    type="datetime-local"
                    class="filter-input"
                    :min="minDateTime"
                  />
                  <small class="form-text text-muted">Pick a preset or set when access should end.</small>
                </div>
              </b-form-group>

              <div class="fm-modal-foot">
                <button type="button" class="fm-btn fm-btn--ghost" @click="showGrantModal = false">Cancel</button>
                <button type="submit" class="fm-btn fm-btn--primary" :disabled="!canRequest">Submit Request</button>
              </div>
            </b-form>
          </div>
        </b-modal>
      </b-overlay>
    </main>

    <!-- ===== DETAIL DRAWER ===== -->
    <detail-drawer
      :open="drawerOpen"
      kind="Service Access"
      :title="drawerTitle"
      :status="drawerStatus"
      :tone="drawerTone"
      :fields="drawerFields"
      @close="closeDrawer">
      <template #footer>
        <template v-if="selectedKind === 'request' && selected.Status === 'pending'">
          <button class="fm-btn fm-btn--primary" @click="drawerAction('approve')">
            <i class="fa-solid fa-check"></i>Approve
          </button>
          <button class="fm-btn fm-btn--danger" @click="drawerAction('reject')">
            <i class="fa-solid fa-xmark"></i>Reject
          </button>
        </template>
        <template v-else-if="selectedKind === 'manage'">
          <button v-if="selected.IsTemporary" class="fm-btn fm-btn--ghost" @click="drawerAction('extend')">
            <i class="fa-solid fa-clock"></i>Extend
          </button>
          <button class="fm-btn fm-btn--danger" @click="drawerAction('revoke')">
            <i class="fa-solid fa-xmark"></i>Revoke
          </button>
        </template>
      </template>
    </detail-drawer>
  </div>
</template>

<script>
import apiClient from '@/http';
import DetailDrawer from '@/components/DetailDrawer.vue';
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import {
  BButton,
  BForm,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTimepicker,
  BFormTextarea,
  BOverlay,
  BButtonGroup,
  BPagination
} from 'bootstrap-vue';
// Import vue-form-wizard components
import VueFormWizard from 'vue-form-wizard';
import { FormWizard, TabContent } from 'vue-form-wizard';

export default {
  components: {
    DetailDrawer,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BButton,
    BOverlay,
    BFormDatepicker,
    BFormTimepicker,
    BFormTextarea,
    BButtonGroup,
    BPagination,
    FormWizard,
    TabContent,
    vSelect
  },
  data() {
    return {
      _isResetting: false, // Flag to track wizard reset state
      activeView: 'services',
      showGrantModal: false,
      drawerOpen: false,
      selected: {},
      selectedKind: 'service',
      newRequest: {
        serviceId: null,
        reason: '',
        isTemporary: false,
        expiresAt: null,
        expiresTime: null,
        expirationDateTime: '',
      },
      newReqServiceId: null,
      newGrant: {
        serviceId: null,
        userId: null,
        isTemporary: false,
        roleId: null,
        expiresAt: null,
        expiresTime: null,
        expirationDateTime: '',
      },
      newGrantRole: null,
      newGrantService: null,
      newGrantUser: null,
      manageAccessUser: null,
      accessRecords: [],
      requests: [],
      serviceAccessList: [],
      serviceAccessFields: [
        {key: 'ServiceName', label: 'Service Name'},
        {key: 'ServiceType', label: 'Service Type'},
        {key: 'ServiceSubType', label: 'Application'},
        {key: 'IsTemporary', label: 'Access Type'},
        {key: 'ExpiresAt', label: 'Expires At'},
        {key: 'Role', label: 'Role'},

      ],
      accessFields: [
        {key: 'ServiceName', label: 'Service Name'},
        {key: 'ServiceType', label: 'Service Type'},
        {key: 'ServiceSubType', label: 'Application'},
        {key: 'IsTemporary', label: 'Access Type'},
        {key: 'ExpiresAt', label: 'Expires At'},
        {key: 'ExpiresTime', label: 'Expires Time'},
        {key: 'Actions', label: 'Actions'}
      ],
      selectedSid: null,
      selectedServiceId: null,
      selectedRoleId: null,
      selectedRequestId: null,
      services: [],
      serviceSearchResults: [],
      serviceSearchLoading: false,
      searchTimeout: null,
      currentPage: 1,
      perPage: 100,
      servicesPage: 1,
      servicesPerPage: 10,
      servicesSearch: '',
      managePage: 1,
      managePerPage: 10,
      manageSearch: '',
      eligibleUsers: [],
      tableColumns: ['Service Name', 'Reason', 'Access Type', 'Expires At', 'Status', 'Actions'],
      totalRequests: 0,
      loading: false,
      isAdmin: false,  // Default to non-admin, will be updated after fetching user details
      minDate: new Date(),
      // minDateTime will be computed to be reactive
      roles: [], // To store the roles for the selected service
      maxDate: new Date(new Date().setDate(new Date().getDate() + 30)),
      
      // New fields for enhanced access management
      selectedAccessIds: [],
      selectAll: false,
      indeterminate: false,
      accessToExtend: null,
      extensionData: {
        expiresAt: null,
        expiresTime: null,
        expirationDateTime: '',
        reason: ''
      },
      bulkExtensionData: {
        expiresAt: null,
        expiresTime: null,
        expirationDateTime: '',
        reason: ''
      },
      bulkRevokeData: {
        reason: ''
      },
      quickDurations: [
        { value: 1, unit: 'hours', label: '1h' },
        { value: 4, unit: 'hours', label: '4h' },
        { value: 8, unit: 'hours', label: '8h' },
        { value: 1, unit: 'days', label: '1d' },
        { value: 3, unit: 'days', label: '3d' },
        { value: 7, unit: 'days', label: '7d' },
        { value: 30, unit: 'days', label: '30d' }
      ],
      enhancedAccessFields: [
        {key: 'selected', label: '', sortable: false, thClass: 'select-column', tdClass: 'select-column'},
        {key: 'ServiceName', label: 'Service Name'},
        {key: 'ServiceType', label: 'Service Type'},
        {key: 'ServiceSubType', label: 'Application'},
        {key: 'IsTemporary', label: 'Access Type'},
        {key: 'ExpiresAt', label: 'Expires At / Time Remaining'},
        {key: 'Actions', label: 'Actions'}
      ]
    };
  },
  computed: {
    viewDescription() {
      if (this.activeView === 'requests') return 'Approve or deny pending access requests.';
      if (this.activeView === 'manage') return 'Review and revoke access for a specific user.';
      return 'Services you can reach, and how long your access lasts.';
    },
    pendingRequests() {
      return (this.requests || []).filter(r => r.Status === 'pending');
    },
    sortedRequests() {
      const order = { pending: 0, approved: 1, denied: 2, rejected: 2 };
      return (this.requests || []).slice().sort((a, b) => {
        const oa = order[a.Status] !== undefined ? order[a.Status] : 3;
        const ob = order[b.Status] !== undefined ? order[b.Status] : 3;
        return oa - ob;
      });
    },
    canGrant() {
      if (!this.newGrantService || !this.newGrantUser) return false;
      if (this.roles.length > 0 && !this.newGrantRole) return false;
      if (this.newGrant.isTemporary && !this.newGrant.expirationDateTime) return false;
      return true;
    },
    canRequest() {
      if (!this.newReqServiceId) return false;
      if (!this.newRequest.reason || !this.newRequest.reason.trim()) return false;
      if (this.newRequest.isTemporary && !this.newRequest.expirationDateTime) return false;
      return true;
    },
    minDateTime() {
      const now = new Date();
      const localNow = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
      return localNow.toISOString().slice(0, 16);
    },
    // Formatted datetime for review displays
    formattedGrantExpiration() {
      if (!this.newGrant.isTemporary || !this.newGrant.expiresAt || !this.newGrant.expiresTime) {
        return 'Never';
      }
      return this.formatDateTime(this.newGrant.expiresAt, this.newGrant.expiresTime);
    },
    formattedRequestExpiration() {
      if (!this.newRequest.isTemporary || !this.newRequest.expiresAt || !this.newRequest.expiresTime) {
        return 'Never';
      }
      return this.formatDateTime(this.newRequest.expiresAt, this.newRequest.expiresTime);
    },
    drawerTitle() {
      const s = this.selected || {};
      if (this.selectedKind === 'request') {
        return (s.Service && s.Service.Name) || '';
      }
      return s.ServiceName || '';
    },
    drawerStatus() {
      const s = this.selected || {};
      if (this.selectedKind === 'request') {
        return s.Status || '';
      }
      return s.IsTemporary ? 'Temporary' : 'Permanent';
    },
    drawerTone() {
      const s = this.selected || {};
      if (this.selectedKind === 'request') {
        return this.requestTone(s);
      }
      return this.accessTone(s);
    },
    drawerFields() {
      const s = this.selected || {};
      if (this.selectedKind === 'request') {
        if (!s.Service) return [];
        return [
          {label: 'Access Type', value: s.IsTemporary ? 'Temporary' : 'Permanent'},
          {label: 'Expires At', value: s.IsTemporary ? this.formatDate(s.RequestEnd) : 'Never'},
          {label: 'Reason', value: s.Reason},
          {label: 'Status', value: s.Status},
          {label: 'Approved By', value: s.ApprovalBy || '—'},
        ];
      }
      if (!s.ServiceName) return [];
      return [
        {label: 'Service Type', value: s.ServiceType},
        {label: 'Application', value: s.ServiceSubType},
        {label: 'Access Type', value: s.IsTemporary ? 'Temporary' : 'Permanent'},
        {label: 'Expires At', value: s.IsTemporary ? this.formatDateTime(s.ExpiresAt, s.ExpiresTime) : 'Never'},
        {label: 'Role', value: s.Role || '—'},
      ];
    },
    filteredServiceAccessList() {
      const q = (this.servicesSearch || '').trim().toLowerCase();
      if (!q) return this.serviceAccessList;
      return this.serviceAccessList.filter(r => this.matchesQuery(r, q));
    },
    pagedServiceAccessList() {
      const start = (this.servicesPage - 1) * this.servicesPerPage;
      return this.filteredServiceAccessList.slice(start, start + this.servicesPerPage);
    },
    filteredAccessRecords() {
      const q = (this.manageSearch || '').trim().toLowerCase();
      if (!q) return this.accessRecords;
      return this.accessRecords.filter(r => this.matchesQuery(r, q));
    },
    pagedAccessRecords() {
      const start = (this.managePage - 1) * this.managePerPage;
      return this.filteredAccessRecords.slice(start, start + this.managePerPage);
    },
  },
  watch: {
    servicesSearch() {
      this.servicesPage = 1;
    },
    manageSearch() {
      this.managePage = 1;
    },
  },
  methods: {
    openGrantModal() {
      if (this.isAdmin) {
        this.resetGrantForm();
      } else {
        this.resetRequestForm();
      }
      this.showGrantModal = true;
    },
    accessTone(row) {
      // Permanent -> neutral, Temporary (pending expiry) -> warn
      return row && row.IsTemporary ? 'warn' : 'neu';
    },
    matchesQuery(row, q) {
      return ['ServiceName', 'ServiceType', 'ServiceSubType', 'Role'].some(
        key => row[key] && String(row[key]).toLowerCase().includes(q)
      );
    },
    requestTone(row) {
      const status = (row && row.Status) || '';
      if (status === 'approved') return 'suc';
      if (status === 'rejected' || status === 'denied') return 'err';
      if (status === 'pending') return 'warn';
      return 'neu';
    },
    selectRequestsView() {
      this.activeView = 'requests';
      this.fetchRequests();
    },
    openRow(row) {
      this.selected = row;
      this.selectedKind = 'service';
      this.drawerOpen = true;
    },
    openRequest(row) {
      this.selected = row;
      this.selectedKind = 'request';
      this.drawerOpen = true;
    },
    openManage(row) {
      this.selected = row;
      this.selectedKind = 'manage';
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    drawerAction(type) {
      const row = this.selected;
      this.closeDrawer();
      if (type === 'approve') {
        this.approveRequest(row);
      } else if (type === 'reject') {
        this.rejectRequest(row.RequestId);
      } else if (type === 'extend') {
        this.showExtendModal(row);
      } else if (type === 'revoke') {
        this.confirmRevoke(row.Sid);
      }
    },
    async loadAccessRecords() {
      if (!this.manageAccessUser) {
        this.accessRecords = [];
        this.selectedAccessIds = [];
        this.updateSelectAllState();
        return;
      }
      try {
        const response = await apiClient.get(`/access-grants?userId=${this.manageAccessUser.value}`);
        this.accessRecords = response.data;
        this.managePage = 1;
        // Reset selections when loading new user's records
        this.selectedAccessIds = [];
        this.updateSelectAllState();
      } catch (error) {
        console.error('Failed to load access records:', error);
        this.showNotification('Failed to load access records', 'danger');
      }
    },
    confirmRevoke(sid) {
      this.selectedSid = sid;
      this.$refs.confirmRevokeModal.show();
    },
    async revokeAccess() {
      if (!this.selectedSid) return;
      try {
        const response = await apiClient.delete(`/access-grants/${this.selectedSid}`);
        this.showNotification('Access revoked successfully', 'success');
        // Optionally, refresh the access records list or remove the revoked item from the list
        await this.loadAccessRecords();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error revoking access';
        this.showNotification(errorMessage, 'danger');
      }
    },
    async fetchUserDetails() {
      try {
        const response = await apiClient.get('/user');
        this.isAdmin = response.data.Role === 'admin';
        if (this.isAdmin) {
          await this.fetchNonAdminUsers();
        }
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching user details';
        this.showNotification(errorMessage, 'danger');
      }
    },
    async fetchServiceAccess() {
      this.loading = true;
      try {
        const response = await apiClient.get('/access-grants');
        this.serviceAccessList = response.data;
        this.servicesPage = 1;
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching service access';
        this.showNotification(errorMessage, 'danger');
      } finally {
        this.loading = false;
      }
    },
    async handleRequestAccess() {
      try {
        this.newRequest.serviceId = this.newReqServiceId.value
        
        // Parse datetime-local format and populate separate fields for backend compatibility
        if (this.newRequest.isTemporary && this.newRequest.expirationDateTime) {
          // datetime-local gives us local time string like "2025-08-04T15:12"
          // Backend expects local time values (it will convert to UTC)
          const [datePart, timePart] = this.newRequest.expirationDateTime.split('T');
          this.newRequest.expiresAt = datePart;
          this.newRequest.expiresTime = timePart + ':00'; // Add seconds
        }
        
        const requestPayload = {
          ServiceName: this.newRequest.serviceId,
          ExpiresAt: this.newRequest.isTemporary ? this.newRequest.expiresAt : '',
          ExpiresTime: this.newRequest.isTemporary ? this.newRequest.expiresTime : '',
          IsTemporary: this.newRequest.isTemporary,
          AccessReason: this.newRequest.reason,
          TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        const response = await apiClient.post('/access-requests', requestPayload);
        
        // Add the new request to the list
        this.requests.push(response.data);
        
        // Update stats
        this.updateStats();
        
        // Show success notification
        this.showNotification('Request submitted successfully', 'success');
        
        // Close the modal and reset the form
        this.showGrantModal = false;
        // Reset the form completely and go back to step 1
        // Use setTimeout to ensure the wizard completes its animation first
        setTimeout(() => {
          this.resetRequestForm();
        }, 300);
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error requesting access';
        if (error.response.status !== 403) {
          this.showNotification(errorMessage, 'danger');
        }
      }
    },
    
    // Method to completely reset the request access form and return to step 1
    resetRequestForm() {
      // Reset all form values
      this.newRequest = {
        serviceId: null,
        reason: '',
        isTemporary: false,
        expiresAt: null,
        expiresTime: null,
      };
      
      // Reset the select component
      this.newReqServiceId = null;
      
      // Set flag to prevent validation errors during reset
      this._isResetting = true;
      
      // Reset wizard to first step if wizard ref exists
      this.$nextTick(() => {
        if (this.$refs.requestWizard) {
          // Temporarily disable validation
          const wizardInstance = this.$refs.requestWizard;
          const originalValidateOnSelect = wizardInstance.validateOnSelect;
          wizardInstance.validateOnSelect = false;
          
          // Reset and navigate
          wizardInstance.reset();
          wizardInstance.navigateToTab(0);
          
          // Restore validation after reset
          setTimeout(() => {
            wizardInstance.validateOnSelect = originalValidateOnSelect;
            this._isResetting = false;
          }, 100);
        } else {
          this._isResetting = false;
        }
      });
    },
    async handleGrantAccess() {
      try {
        this.newGrant.serviceId = this.newGrantService.value
        this.newGrant.userId = this.newGrantUser.value
        if (this.newGrantRole) {
          this.newGrant.roleId = this.newGrantRole.value
        }
        
        // Parse datetime-local format and populate separate fields for backend compatibility  
        if (this.newGrant.isTemporary && this.newGrant.expirationDateTime) {
          // datetime-local gives us local time string like "2025-08-04T15:12"
          // Backend expects local time values (it will convert to UTC)
          const [datePart, timePart] = this.newGrant.expirationDateTime.split('T');
          this.newGrant.expiresAt = datePart;
          this.newGrant.expiresTime = timePart + ':00'; // Add seconds
        }
        
        const grantPayload = {
          ServiceName: this.newGrant.serviceId,
          UserId: this.newGrant.userId,
          RoleId: this.newGrant.roleId,
          ExpiresAt: this.newGrant.isTemporary ? this.newGrant.expiresAt : '',
          ExpiresTime: this.newGrant.isTemporary ? this.newGrant.expiresTime : '',
          IsTemporary: this.newGrant.isTemporary,
          TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        await apiClient.post('/access-grants', grantPayload);
        
        // Refresh the access records
        await this.fetchServiceAccess();
        
        // Show success notification
        this.showNotification('Access granted successfully', 'success');

        // Close the modal and reset the form
        this.showGrantModal = false;
        // Reset all form fields completely and go back to step 1
        // Use setTimeout to ensure the wizard completes its animation first
        setTimeout(() => {
          this.resetGrantForm();
        }, 300);
      } catch (error) {
        console.log(error);
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error granting access';
        this.showNotification(errorMessage, 'danger');
      }
    },
    
    // Method to completely reset the grant access form and return to step 1
    resetGrantForm() {
      // Reset all form values
      this.newGrant = {
        serviceId: null,
        userId: null,
        roleId: null,
        isTemporary: false,
        expiresAt: null,
        expiresTime: null,
      };
      
      // Reset the select components
      this.newGrantService = null;
      this.newGrantUser = null;
      this.newGrantRole = null;
      
      // Reset roles array
      this.roles = [];
      
      // Set flag to prevent validation errors during reset
      this._isResetting = true;
      
      // Reset wizard to first step if wizard ref exists
      this.$nextTick(() => {
        if (this.$refs.grantWizard) {
          // Temporarily disable validation
          const wizardInstance = this.$refs.grantWizard;
          const originalValidateOnSelect = wizardInstance.validateOnSelect;
          wizardInstance.validateOnSelect = false;
          
          // Reset and navigate
          wizardInstance.reset();
          wizardInstance.navigateToTab(0);
          
          // Restore validation after reset
          setTimeout(() => {
            wizardInstance.validateOnSelect = originalValidateOnSelect;
            this._isResetting = false;
          }, 100);
        } else {
          this._isResetting = false;
        }
      });
    },
    async fetchRequests() {
      this.loading = true;
      try {
        const response = await apiClient.get('/access-requests');
        this.requests = response.data;
        this.updateStats();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching requests';
        if (error.response.status !== 403) {
          this.showNotification(errorMessage, 'danger');
        }
      } finally {
        this.loading = false;
      }
    },
    async searchServices(query) {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // If query is empty, clear results
      if (!query || query.trim().length === 0) {
        this.serviceSearchResults = [];
        this.serviceSearchLoading = false;
        return;
      }
      
      // Set loading state
      this.serviceSearchLoading = true;
      
      // Debounce the search to avoid too many API calls
      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await apiClient.get(`/services?search=${encodeURIComponent(query.trim())}&page=1&pageSize=20`);
          
          // Handle the API response structure
          const services = response.data.services || response.data || [];
          
          this.serviceSearchResults = services.map(service => ({
            value: service.ServiceName,
            text: service.ServiceName,
            label: service.ServiceName
          }));
        } catch (error) {
          console.error('Error searching services:', error);
          this.serviceSearchResults = [];
          // Only show error if it's not a network timeout or user cancelled request
          if (error.code !== 'ERR_CANCELLED' && error.code !== 'ECONNABORTED') {
            this.showNotification('Error searching services. Please try again.', 'warning');
          }
        } finally {
          this.serviceSearchLoading = false;
        }
      }, 300); // 300ms debounce delay
    },
    
    async fetchServices() {
      // This method is kept for backward compatibility but now just initializes empty array
      this.services = [];
      this.serviceSearchResults = [];
    },
    async fetchRolesForService() {
      try {
        this.newGrant.serviceId = this.newGrantService.value
        if (!this.newGrant.serviceId) {
          this.roles = []; // Reset roles if no service is selected
          return;
        }

        console.log(this.newGrant)

        // Assuming the API endpoint is `/services/{serviceId}/roles`
        const response = await apiClient.get(`/service/${this.newGrant.serviceId}/roles`);
        this.roles = response.data.map(role => ({
          value: role.ResourceId,
          text: role.Name,
          label: role.Name
        }));
      } catch (error) {
        console.error('Error fetching roles:', error);
        this.roles = [];
      }
    },
    async fetchNonAdminUsers() {
      try {
        const response = await apiClient.get(`/users?page=${this.currentPage}&pageSize=${this.perPage}`);
        this.eligibleUsers = response.data.users
          .map(user => ({
            value: user.UserId,
            text: user.Name || user.Email,
            label: user.Name || user.Email,
          }));
      } catch (error) {
        this.showNotification('Error fetching non-admin users', 'danger');
      }
    },
    async approveRequest(request) {
      this.selectedRequestId = request.RequestId;
      this.selectedServiceId = request.Service.Name;

      // Fetch roles for the selected service
      try {
        const response = await apiClient.get(`/service/${this.selectedServiceId}/roles`);
        this.roles = response.data.map(role => ({
          value: role.ResourceId,
          text: role.Name,
          label: role.Name,
        }));

        // If there are roles, show the modal to assign the role
        if (this.roles.length > 0) {
          this.$refs.assignRoleModal.show();
        } else {
          // If no roles, directly approve the request
          await this.confirmApproval();
        }
      } catch (error) {
        this.showNotification('Error fetching roles', 'danger');
      }
    },

    // Function to confirm approval (with or without role)
    async confirmApproval() {
      // If no role selection is needed (roles array is empty), skip roleId
      const approvalPayload = {
        action: 'approve',

      };

      if (this.roles.length > 0 && this.selectedRoleId) {
        approvalPayload.roleId = this.selectedRoleId.value;
      }

      try {
        await apiClient.post(`/access-requests/${this.selectedRequestId}/actions`, approvalPayload);

        // Fetch the updated requests list after approval
        await this.fetchRequests();
        this.showNotification('Request approved successfully', 'success');
      } catch (error) {
        this.showNotification('Error approving request', 'danger');
      }

      // Reset selection
      this.selectedRequestId = null;
      this.selectedServiceId = null;
      this.selectedRoleId = null;
    },

    async rejectRequest(requestId) {
      try {
        await apiClient.post(`/access-requests/${requestId}/actions`, {action: 'reject'});
        await this.fetchRequests();
        this.showNotification('Request Rejected', 'success');
      } catch (error) {
        this.showNotification('Error rejecting request', 'danger');
      }
    },
    // This method is now replaced by more specific resetGrantForm and resetRequestForm methods
    // Keeping it for backward compatibility but it calls the specific methods
    resetForm(type) {
      if (type === 'request') {
        this.resetRequestForm();
      } else if (type === 'grant') {
        this.resetGrantForm();
      }
    },
    updateStats() {
      this.totalRequests = this.requests.length;
    },
    formatDate(dateString) {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      };
      return new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
    },
    formatDateTime(dateString, timeString) {
      // Backend sends UTC date/time, so we need to explicitly treat it as UTC
      const utcDate = new Date(dateString + 'T' + timeString + 'Z'); // Add 'Z' to indicate UTC
      return utcDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
    },
    showNotification(message, type) {
      this.$bvToast.toast(message, {
        title: `Info`,
        variant: type,
        solid: true
      })
    },
    
    // Handle wizard validation errors
    handleWizardError(errorData) {
      // Only show validation errors if they're not part of a reset/navigation
      if (!this._isResetting) {
        console.log('Wizard validation error:', errorData);
        // Show a subtle warning on the form instead of a toast notification
        // This is less disruptive to the user experience
      }
    },
    
    // Quick duration methods
    setQuickDuration(duration, type) {
      const now = new Date();
      let targetDate;
      
      if (duration.unit === 'hours') {
        targetDate = new Date(now.getTime() + (duration.value * 60 * 60 * 1000));
      } else if (duration.unit === 'days') {
        targetDate = new Date(now.getTime() + (duration.value * 24 * 60 * 60 * 1000));
      }
      
      // For datetime-local input, we need to display the time as it would appear locally
      // but targetDate is already in local time (JavaScript Date objects are always local)
      // We just need to format it for datetime-local input (YYYY-MM-DDTHH:MM)
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const hours = String(targetDate.getHours()).padStart(2, '0');
      const minutes = String(targetDate.getMinutes()).padStart(2, '0');
      const datetimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      // For backend compatibility, store LOCAL time values (backend will convert to UTC)
      const dateStr = `${year}-${month}-${day}`;
      const timeStr = `${hours}:${minutes}:00`;
      
      if (type === 'grant') {
        this.newGrant.expirationDateTime = datetimeString;
        this.newGrant.expiresAt = dateStr;
        this.newGrant.expiresTime = timeStr;
      } else if (type === 'request') {
        this.newRequest.expirationDateTime = datetimeString;
        this.newRequest.expiresAt = dateStr;
        this.newRequest.expiresTime = timeStr;
      }
    },
    
    setExtensionQuickDuration(duration) {
      const now = new Date();
      let targetDate;
      
      if (duration.unit === 'hours') {
        targetDate = new Date(now.getTime() + (duration.value * 60 * 60 * 1000));
      } else if (duration.unit === 'days') {
        targetDate = new Date(now.getTime() + (duration.value * 24 * 60 * 60 * 1000));
      }
      
      // Format as local time for the user's timezone
      // The backend will properly convert this local time to UTC using the provided timezone
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const hours = String(targetDate.getHours()).padStart(2, '0');
      const minutes = String(targetDate.getMinutes()).padStart(2, '0');
      
      // Set both formats for compatibility
      this.extensionData.expiresAt = `${year}-${month}-${day}`;
      this.extensionData.expiresTime = `${hours}:${minutes}:00`;
      this.extensionData.expirationDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      console.log(`[DEBUG] Quick duration ${duration.value} ${duration.unit} set:`, {
        current: now.toLocaleString(),
        target: targetDate.toLocaleString(),
        expiresAt: this.extensionData.expiresAt,
        expiresTime: this.extensionData.expiresTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    },
    
    setBulkExtensionQuickDuration(duration) {
      const now = new Date();
      let targetDate;
      
      if (duration.unit === 'hours') {
        targetDate = new Date(now.getTime() + (duration.value * 60 * 60 * 1000));
      } else if (duration.unit === 'days') {
        targetDate = new Date(now.getTime() + (duration.value * 24 * 60 * 60 * 1000));
      }
      
      // Store local time values for backend (backend will convert to UTC)
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const hours = String(targetDate.getHours()).padStart(2, '0');
      const minutes = String(targetDate.getMinutes()).padStart(2, '0');
      
      // Set both formats for compatibility
      this.bulkExtensionData.expiresAt = `${year}-${month}-${day}`;
      this.bulkExtensionData.expiresTime = `${hours}:${minutes}:00`;
      this.bulkExtensionData.expirationDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    
    // Time remaining methods
    getTimeRemaining(accessItem) {
      if (!accessItem.IsTemporary) return '';
      
      const now = new Date();
      // Backend sends UTC time, so we need to explicitly treat it as UTC
      const expirationDate = new Date(accessItem.ExpiresAt + 'T' + accessItem.ExpiresTime + 'Z');
      const diffMs = expirationDate.getTime() - now.getTime();
      
      // Debug logging for timezone issues
      console.log(`[DEBUG] Time remaining calculation for ${accessItem.ServiceName}:`, {
        expiresAt: accessItem.ExpiresAt,
        expiresTime: accessItem.ExpiresTime,
        expirationDate: expirationDate.toString(),
        expirationUTC: expirationDate.toUTCString(),
        now: now.toString(),
        nowUTC: now.toUTCString(),
        diffMs: diffMs,
        diffHours: diffMs / (1000 * 60 * 60)
      });
      
      if (diffMs <= 0) return 'Expired';
      
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} remaining`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} remaining`;
      } else {
        return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} remaining`;
      }
    },
    
    getTimeRemainingClass(accessItem) {
      if (!accessItem.IsTemporary) return '';
      
      const now = new Date();
      // Backend sends UTC time, so we need to explicitly treat it as UTC
      const expirationDate = new Date(accessItem.ExpiresAt + 'T' + accessItem.ExpiresTime + 'Z');
      const diffMs = expirationDate.getTime() - now.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      
      if (diffMs <= 0) return 'text-danger font-weight-bold';
      if (diffHours <= 1) return 'text-danger';
      if (diffHours <= 24) return 'text-warning';
      return 'text-muted';
    },
    
    // Selection methods
    onRowSelected(items) {
      this.selectedAccessIds = items.map(item => item.Sid);
      this.updateSelectAllState();
    },
    
    toggleRowSelection(sid) {
      const index = this.selectedAccessIds.indexOf(sid);
      if (index > -1) {
        this.selectedAccessIds.splice(index, 1);
      } else {
        this.selectedAccessIds.push(sid);
      }
      this.updateSelectAllState();
    },
    
    selectAllRows(checked) {
      if (checked) {
        this.selectedAccessIds = this.accessRecords.map(record => record.Sid);
      } else {
        this.selectedAccessIds = [];
      }
    },
    
    updateSelectAllState() {
      const totalItems = this.accessRecords.length;
      const selectedItems = this.selectedAccessIds.length;
      
      if (selectedItems === 0) {
        this.selectAll = false;
        this.indeterminate = false;
      } else if (selectedItems === totalItems) {
        this.selectAll = true;
        this.indeterminate = false;
      } else {
        this.selectAll = false;
        this.indeterminate = true;
      }
    },
    
    // Extension methods
    showExtendModal(accessItem) {
      this.accessToExtend = accessItem;
      this.extensionData = {
        expiresAt: null,
        expiresTime: null,
        expirationDateTime: '',
        reason: ''
      };
      this.$refs.extendAccessModal.show();
    },
    
    async extendAccess() {
      // Check if we have either quick duration data or datetime input
      const hasQuickData = this.extensionData.expiresAt && this.extensionData.expiresTime;
      const hasDateTimeInput = this.extensionData.expirationDateTime;
      
      if (!this.accessToExtend || (!hasQuickData && !hasDateTimeInput)) {
        this.showNotification('Please select a new expiration date and time', 'warning');
        return;
      }
      
      try {
        let expiresAt, expiresTime;
        
        if (hasDateTimeInput) {
          // Parse datetime-local format and populate separate fields for backend compatibility
          const [datePart, timePart] = this.extensionData.expirationDateTime.split('T');
          expiresAt = datePart;
          expiresTime = timePart + ':00'; // Add seconds
        } else {
          // Use quick duration data
          expiresAt = this.extensionData.expiresAt;
          expiresTime = this.extensionData.expiresTime;
        }
        
        const extensionPayload = {
          NewExpirationDate: expiresAt,
          NewExpirationTime: expiresTime,
          TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ExtensionReason: this.extensionData.reason
        };
        
        console.log(`[DEBUG] Sending extension request:`, extensionPayload);
        
        await apiClient.post(`/access-grants/${this.accessToExtend.Sid}/extend`, extensionPayload);
        this.showNotification('Access extended successfully', 'success');
        await this.loadAccessRecords();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error extending access';
        this.showNotification(errorMessage, 'danger');
      }
    },
    
    // Bulk operation methods
    showBulkExtendModal() {
      this.bulkExtensionData = {
        expiresAt: null,
        expiresTime: null,
        expirationDateTime: '',
        reason: ''
      };
      this.$refs.bulkExtendModal.show();
    },
    
    showBulkRevokeModal() {
      this.bulkRevokeData = {
        reason: ''
      };
      this.$refs.bulkRevokeModal.show();
    },
    
    async bulkExtendAccess() {
      // Check if we have either quick duration data or datetime input
      const hasQuickData = this.bulkExtensionData.expiresAt && this.bulkExtensionData.expiresTime;
      const hasDateTimeInput = this.bulkExtensionData.expirationDateTime;
      
      if (!hasQuickData && !hasDateTimeInput) {
        this.showNotification('Please select a new expiration date and time', 'warning');
        return;
      }
      
      try {
        let expiresAt, expiresTime;
        
        if (hasDateTimeInput) {
          // Parse datetime-local format and populate separate fields for backend compatibility
          const [datePart, timePart] = this.bulkExtensionData.expirationDateTime.split('T');
          expiresAt = datePart;
          expiresTime = timePart + ':00'; // Add seconds
        } else {
          // Use quick duration data
          expiresAt = this.bulkExtensionData.expiresAt;
          expiresTime = this.bulkExtensionData.expiresTime;
        }
        
        const bulkPayload = {
          ServiceAccessIds: this.selectedAccessIds,
          NewExpirationDate: expiresAt,
          NewExpirationTime: expiresTime,
          TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          Reason: this.bulkExtensionData.reason
        };
        
        const response = await apiClient.post('/access-grants/bulk-extend', bulkPayload);
        
        const successCount = response.data.success_count || 0;
        const errorCount = response.data.error_count || 0;
        
        if (errorCount > 0) {
          this.showNotification(`Bulk extension completed: ${successCount} succeeded, ${errorCount} failed`, 'warning');
        } else {
          this.showNotification(`Successfully extended access for ${successCount} items`, 'success');
        }
        
        this.selectedAccessIds = [];
        this.updateSelectAllState();
        await this.loadAccessRecords();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error extending access';
        this.showNotification(errorMessage, 'danger');
      }
    },
    
    async bulkRevokeAccess() {
      try {
        const bulkPayload = {
          ServiceAccessIds: this.selectedAccessIds,
          Reason: this.bulkRevokeData.reason
        };
        
        const response = await apiClient.post('/access-grants/bulk-revoke', bulkPayload);
        
        const successCount = response.data.success_count || 0;
        const errorCount = response.data.error_count || 0;
        
        if (errorCount > 0) {
          this.showNotification(`Bulk revocation completed: ${successCount} succeeded, ${errorCount} failed`, 'warning');
        } else {
          this.showNotification(`Successfully revoked access for ${successCount} items`, 'success');
        }
        
        this.selectedAccessIds = [];
        this.updateSelectAllState();
        await this.loadAccessRecords();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error revoking access';
        this.showNotification(errorMessage, 'danger');
      }
    },
  },
  mounted() {
    this.fetchUserDetails();
    this.fetchRequests();
    this.fetchServices(); // Now just initializes empty arrays
    this.fetchServiceAccess();
  },
  
  beforeDestroy() {
    // Clean up any pending search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
};
</script>
<style scoped>
.fm-tab-count {
  font-size: 11px;
  font-weight: 600;
  color: var(--fm-slate-400);
  background: var(--fm-slate-100);
  border-radius: 10px;
  padding: 1px 7px;
  line-height: 1.5;
}
.fm-tab.fm-tab--active .fm-tab-count {
  color: var(--fm-accent);
  background: var(--fm-accent-soft);
}

.content {
  padding: 20px;
}

/* Metric Cards */
.metric-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  min-height: 120px;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}

.metric-card--primary {
  border-left: 4px solid #667eea;
}

.metric-card--success {
  border-left: 4px solid #10B981;
}

.metric-card--info {
  border-left: 4px solid #06B6D4;
}

.metric-card--warning {
  border-left: 4px solid #F59E0B;
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 24px;
  color: white;
}

.metric-card--primary .metric-icon {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.metric-card--success .metric-icon {
  background: linear-gradient(135deg, #10B981, #059669);
}

.metric-card--info .metric-icon {
  background: linear-gradient(135deg, #06B6D4, #0891b2);
}

.metric-card--warning .metric-icon {
  background: linear-gradient(135deg, #F59E0B, #D97706);
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  color: #2c3e50;
}

.metric-label {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #4a5568;
}

.metric-subtitle {
  color: #718096;
  font-size: 0.9rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .metric-card {
    padding: 16px;
    min-height: 100px;
  }
  
  .metric-icon {
    width: 50px;
    height: 50px;
    margin-right: 16px;
    font-size: 20px;
  }
  
  .metric-value {
    font-size: 2rem;
  }
}

.card-title {
  font-size: 24px;
  font-weight: bold;
}

.cardl {
  min-height: 500px;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-fill {
  margin-right: 5px;
}

.text-center {
  text-align: center;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.b-modal {
  max-width: 500px;
}

/* Wizard Styles */
.wizard-card {
  box-shadow: 0 15px 30px 0 rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  border: none;
  overflow: hidden;
}

.wizard-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #eaeaea;
  padding: 20px;
}

.wizard-tab-content {
  padding: 20px 40px;
  max-width: 800px;
  margin: 0 auto;
}

.wizard-step-title {
  color: #3498db;
  font-weight: 600;
  font-size: 1.25rem;
  margin-bottom: 10px;
}

.wizard-step-description {
  color: #6c757d;
  margin-bottom: 20px;
}

.wizard-select {
  margin-bottom: 10px;
}

.date-time-selector {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.date-picker, .time-picker {
  flex: 1;
}

/* Review section styling */
.review-container {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #eaeaea;
}

.review-item {
  padding: 10px 0;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  align-items: flex-start;
}

.review-item:last-child {
  border-bottom: none;
}

.review-label {
  font-weight: 600;
  color: #6c757d;
  width: 140px;
  flex-shrink: 0;
}

.review-value {
  color: #212529;
}

.review-value.reason-text {
  white-space: pre-line;
}

/* Override vue-form-wizard styles */
.custom-wizard ::v-deep .wizard-nav {
  padding: 15px 0;
}

.custom-wizard ::v-deep .wizard-nav-pills {
  margin: 30px 0 0;
  display: flex;
  justify-content: space-between;
  position: relative;
}

.custom-wizard ::v-deep .wizard-progress-with-circle {
  position: absolute !important;
  top: 18px !important;
  height: 2px !important;
  background-color: #e9ecef !important;
  width: 100% !important;
  left: 0 !important;
}

.custom-wizard ::v-deep .wizard-progress-with-circle .wizard-progress-bar {
  background-color: #2563EB !important;
  height: 2px !important;
}

.custom-wizard ::v-deep .wizard-nav-pills > li {
  width: auto !important;
  min-width: 100px;
}

.custom-wizard ::v-deep .wizard-nav-pills > li > a {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #6c757d !important;
  padding: 5px 0 !important;
}

.custom-wizard ::v-deep .wizard-nav-pills > li.active > a {
  color: #495057 !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.custom-wizard ::v-deep .wizard-nav-pills > li.active > a .wizard-icon {
  color: white !important;
}

.custom-wizard ::v-deep .wizard-btn {
  border-radius: 4px !important;
  padding: 8px 16px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  box-shadow: none !important;
  font-size: 14px !important;
  min-width: 100px;
}

.custom-wizard ::v-deep .wizard-btn-finish {
  background-color: #2563EB !important;
}

.custom-wizard ::v-deep .wizard-btn-next {
  background-color: #2563EB !important;
}

.custom-wizard ::v-deep .wizard-btn-previous {
  background-color: #6c757d !important;
}

.custom-wizard ::v-deep .wizard-tab-content {
  min-height: 320px !important;
  padding: 20px 0 !important;
}

.custom-wizard ::v-deep .wizard-card-footer {
  padding: 15px 0 !important;
}

.custom-wizard ::v-deep .wizard-header {
  display: none !important; /* Hide the default wizard header since we have our own */
}

.custom-wizard ::v-deep .wizard-icon-circle {
  width: 36px !important;
  height: 36px !important;
  border: 2px solid #e9ecef !important;
  background-color: white !important;
  position: relative !important;
  z-index: 10 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.custom-wizard ::v-deep .wizard-icon-circle.checked {
  background-color: #2563EB !important;
  border-color: #2563EB !important;
}

.custom-wizard ::v-deep .wizard-icon {
  font-size: 16px !important;
  color: #adb5bd !important;
}

.custom-wizard ::v-deep .wizard-icon-circle.checked .wizard-icon {
  color: white !important;
}

.custom-wizard ::v-deep .stepTitle {
  color: #6c757d !important;
  font-weight: 500 !important;
  margin-top: 10px !important;
  font-size: 13px !important;
}

.custom-wizard ::v-deep .active .stepTitle {
  color: #212529 !important;
}

/* Improve form element styles */
.wizard-select, .form-control {
  font-size: 14px !important;
  color: #495057 !important;
}

.form-group label {
  font-weight: 500 !important;
  color: #495057 !important;
  font-size: 14px !important;
}

small.form-text {
  color: #6c757d !important;
  font-size: 12px !important;
}

.wizard-step-title {
  color: #2563EB !important;
  font-weight: 600 !important;
  font-size: 18px !important;
  margin-bottom: 10px !important;
}

.wizard-step-description {
  color: #6c757d !important;
  margin-bottom: 25px !important;
  font-size: 14px !important;
}

/* v-select improvements */
::v-deep .v-select .vs__dropdown-toggle {
  border-radius: 4px !important;
  padding: 6px 0 !important;
  border-color: #ced4da !important;
}

::v-deep .v-select .vs__selected {
  font-weight: 500 !important;
  color: #495057 !important;
}

::v-deep .vs__dropdown-menu {
  border-radius: 4px !important;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
}

/* Review section improvements */
.review-container {
  background-color: #f8f9fa !important;
  border-radius: 6px !important;
  padding: 20px !important;
  border: 1px solid #e9ecef !important;
}

.review-item {
  padding: 12px 0 !important;
  border-bottom: 1px solid #e9ecef !important;
  display: flex !important;
  align-items: flex-start !important;
}

.review-label {
  font-weight: 600 !important;
  color: #6c757d !important;
  width: 140px !important;
  flex-shrink: 0 !important;
}

.review-value {
  color: #212529 !important;
  font-weight: 400 !important;
}

/* Search functionality improvements */
::v-deep .vs__spinner {
  border-left-color: #2563EB !important;
}

::v-deep .vs__dropdown-menu {
  max-height: 300px !important;
  overflow-y: auto !important;
}

::v-deep .vs__no-options {
  color: #6c757d !important;
  font-size: 14px !important;
  padding: 12px !important;
  background-color: #f8f9fa !important;
  border-radius: 4px !important;
  margin: 4px !important;
}

::v-deep .vs__search::placeholder {
  color: #6c757d !important;
}

::v-deep .vs__selected {
  color: #495057 !important;
  font-weight: 500 !important;
}

/* Loading state improvements */
::v-deep .vs__dropdown-toggle {
  position: relative !important;
}

::v-deep .vs__actions {
  padding: 4px 6px !important;
}

::v-deep .vs__search {
  font-size: 14px !important;
  padding: 8px !important;
}

/* Manage Access Section Styles */
.bulk-actions {
  display: flex;
  align-items: center;
}

.bulk-action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.select-column {
  width: 40px;
  text-align: center;
}

/* Time remaining styles */
.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-muted {
  color: #6c757d !important;
}

.font-weight-bold {
  font-weight: 700 !important;
}

/* Extension modal improvements */
.quick-duration-buttons {
  margin-bottom: 1rem;
}

.quick-duration-btn {
  margin: 2px;
  border-radius: 4px;
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
}

.quick-duration-btn:hover {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

@media (max-width: 768px) {
  .bulk-action-buttons {
    flex-direction: column;
    gap: 4px;
  }
}

/* ============================================================
   Grant / Request wizard — design-system refinement.
   Derived from Acceldata tokens; placed last so it supersedes
   the ad-hoc rules above. Blue is reserved for progress/active
   state only; action buttons use the app's slate primary.
   ============================================================ */
.wizard-card { box-shadow: none !important; border: none !important; border-radius: 0 !important; }

/* Step content heading + description */
.wizard-step-title {
  color: var(--fm-text) !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  letter-spacing: -0.01em !important;
  margin-bottom: 4px !important;
}
.wizard-step-description {
  color: var(--fm-text-secondary) !important;
  font-size: 13.5px !important;
  margin-bottom: 20px !important;
}

/* Progress rail */
.custom-wizard ::v-deep .wizard-progress-with-circle {
  top: 17px !important;
  height: 2px !important;
  background-color: var(--fm-border) !important;
}
.custom-wizard ::v-deep .wizard-progress-with-circle .wizard-progress-bar {
  background-color: var(--fm-accent) !important;
  height: 2px !important;
  transition: width .24s cubic-bezier(.2,0,0,1) !important;
}

/* Step markers — signature: a quiet accent-soft ring on the active step */
.custom-wizard ::v-deep .wizard-icon-circle {
  width: 34px !important; height: 34px !important;
  border: 1.5px solid var(--fm-border-strong) !important;
  background: #fff !important;
  transition: border-color .2s ease, background-color .2s ease, box-shadow .2s ease !important;
}
.custom-wizard ::v-deep .wizard-icon { font-size: 14px !important; color: var(--fm-text-muted) !important; }
.custom-wizard ::v-deep .wizard-icon-circle.checked {
  background: var(--fm-accent) !important;
  border-color: var(--fm-accent) !important;
  box-shadow: 0 0 0 4px var(--fm-accent-soft) !important;
}
.custom-wizard ::v-deep .wizard-icon-circle.checked .wizard-icon { color: #fff !important; }
.custom-wizard ::v-deep .stepTitle {
  color: var(--fm-text-muted) !important;
  font-weight: 500 !important;
  font-size: 12.5px !important;
  margin-top: 10px !important;
}
.custom-wizard ::v-deep .active .stepTitle { color: var(--fm-text) !important; }

/* Nav buttons — slate primary + ghost, matching every app CTA */
.custom-wizard ::v-deep .wizard-btn {
  border-radius: var(--fm-r-control) !important;
  height: 38px !important;
  padding: 0 18px !important;
  font: 500 13px/36px 'Inter', sans-serif !important;
  box-shadow: none !important;
  border: 1px solid transparent !important;
  transition: background-color .12s ease, border-color .12s ease !important;
}
.custom-wizard ::v-deep .wizard-btn-next,
.custom-wizard ::v-deep .wizard-btn-finish {
  background-color: var(--fm-primary) !important;
  color: #fff !important;
  border-color: var(--fm-primary) !important;
}
.custom-wizard ::v-deep .wizard-btn-next:hover,
.custom-wizard ::v-deep .wizard-btn-finish:hover {
  background-color: var(--fm-primary-hover) !important;
  border-color: var(--fm-primary-hover) !important;
}
.custom-wizard ::v-deep .wizard-btn-previous {
  background-color: #fff !important;
  color: var(--fm-slate-700) !important;
  border-color: var(--fm-border) !important;
}
.custom-wizard ::v-deep .wizard-btn-previous:hover { background-color: var(--fm-slate-100) !important; }
.custom-wizard ::v-deep .wizard-card-footer { padding: 8px 0 0 !important; }
.custom-wizard ::v-deep .wizard-tab-content { min-height: 240px !important; padding: 24px 0 8px !important; }
.custom-wizard ::v-deep .wizard-nav-pills { margin-top: 6px !important; }

/* Inputs / v-select — token borders + accent focus ring */
.wizard-tab-content ::v-deep .v-select .vs__dropdown-toggle {
  border-radius: var(--fm-r-control) !important;
  border-color: var(--fm-border) !important;
  min-height: 38px !important;
  padding: 3px 4px !important;
}
.wizard-tab-content ::v-deep .v-select.vs--open .vs__dropdown-toggle {
  border-color: var(--fm-accent) !important;
  box-shadow: 0 0 0 3px rgba(37,99,235,.12) !important;
}
.wizard-tab-content ::v-deep .vs__dropdown-menu {
  border-radius: var(--fm-r-control) !important;
  border-color: var(--fm-border) !important;
  box-shadow: var(--fm-shadow-pop) !important;
}
.wizard-tab-content ::v-deep .vs__dropdown-option--highlight { background: var(--fm-accent) !important; }
.wizard-tab-content ::v-deep .vs__selected,
.wizard-tab-content ::v-deep .form-control { color: var(--fm-text) !important; }
.wizard-tab-content ::v-deep .form-control:focus {
  border-color: var(--fm-accent) !important;
  box-shadow: 0 0 0 3px rgba(37,99,235,.12) !important;
}

/* Review step — mirrors the detail-drawer label/value list */
.review-container {
  background: var(--fm-slate-50) !important;
  border: 1px solid var(--fm-border) !important;
  border-radius: var(--fm-r-card) !important;
  padding: 4px 16px !important;
}
.review-item { padding: 13px 0 !important; border-bottom: 1px solid var(--fm-slate-100) !important; }
.review-label { color: var(--fm-text-secondary) !important; font-weight: 500 !important; font-size: 13px !important; width: 150px !important; }
.review-value { color: var(--fm-text) !important; font-size: 13.5px !important; font-weight: 500 !important; }

/* Quick-duration chips — hover uses the accent, not a stray blue */
.quick-duration-btn:hover {
  background-color: var(--fm-accent) !important;
  border-color: var(--fm-accent) !important;
  color: #fff !important;
}

/* ============================================================
   Access — single-form modal (replaces the multi-step wizard)
   ============================================================ */
.access-form { padding: 2px 0; }
.access-form__intro {
  font-size: 13px;
  color: var(--fm-text-secondary);
  margin: 0 0 18px;
}
.access-form .form-group { margin-bottom: 18px; }
.access-form__switch { font-size: 13.5px; }
.access-form__duration {
  margin-top: 12px;
  padding: 14px;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-r-control);
  background: var(--fm-slate-50);
}
.access-form__duration .quick-duration-buttons { margin-bottom: 10px; }
.access-form__duration .filter-input { width: 100%; }

/* v-select tokenized to match the app's inputs */
.access-form ::v-deep .v-select .vs__dropdown-toggle {
  border-radius: var(--fm-r-control);
  border-color: var(--fm-border);
  min-height: 38px;
  padding: 3px 4px;
}
.access-form ::v-deep .v-select.vs--open .vs__dropdown-toggle {
  border-color: var(--fm-accent);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
}
.access-form ::v-deep .vs__dropdown-menu {
  border-radius: var(--fm-r-control);
  border-color: var(--fm-border);
  box-shadow: var(--fm-shadow-pop);
}
.access-form ::v-deep .vs__dropdown-option--highlight { background: var(--fm-accent); }

/* ============================================================
   Access page — approval inbox, manage bar, empty states
   ============================================================ */
/* Pending request rows carry a quiet amber edge = needs action */
.req-row--pending { box-shadow: inset 3px 0 0 var(--fm-warning); }
.req-actions { display: flex; gap: 8px; flex: 0 0 auto; }
.req-actions .fm-btn { height: 30px; padding: 0 12px; font-size: 12.5px; }
.req-actions .fm-btn i { font-size: 11px; }

/* Manage access — user picker + selection bar */
.manage-picker { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.manage-picker__label { font-size: 13px; font-weight: 500; color: var(--fm-text-secondary); margin: 0; }
.manage-picker__select { flex: 1; max-width: 420px; }
.manage-picker ::v-deep .v-select .vs__dropdown-toggle {
  border-radius: var(--fm-r-control); border-color: var(--fm-border); min-height: 38px;
}
.manage-picker ::v-deep .v-select.vs--open .vs__dropdown-toggle {
  border-color: var(--fm-accent); box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
}
.manage-bar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 9px 12px; margin-bottom: 4px;
  background: var(--fm-slate-50);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-r-control);
  font-size: 13px;
}
.manage-bar__actions { display: flex; gap: 8px; }
.manage-bar__actions .fm-btn { height: 30px; padding: 0 12px; font-size: 12.5px; }
.fm-row-check { flex: 0 0 auto; display: flex; align-items: center; }

/* Directional empty states */
.access-empty {
  text-align: center;
  padding: 56px 24px;
  border: 1px dashed var(--fm-border);
  border-radius: var(--fm-r-card);
}
.access-empty i { font-size: 24px; color: var(--fm-slate-300); }
.access-empty__title { margin-top: 12px; font-size: 15px; font-weight: 600; color: var(--fm-text); }
.access-empty__sub { margin-top: 4px; font-size: 13px; color: var(--fm-text-muted); }
</style>

<style>
/* The CSS import should be handled in the main.js file instead of here */
</style>

